import React, { SyntheticEvent, useCallback, useEffect, useMemo, useState } from "react";

import { Form, Icon, Modal, TextArea, TextAreaProps } from "semantic-ui-react";

import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import SearchBoxWithKey from "react-lib/appcon/common/SearchBoxWithKey";

import ModConfirm from "react-lib/apps/common/cnmi/ModConfirm";
import EmployeeToken from "react-lib/apps/common/EmployeeToken";
import SnackMessage from "react-lib/apps/common/SnackMessage";
import TimeComboBox from "react-lib/apps/common/TimeComboBox";

import CardScannedDocument from "react-lib/apps/HISV3/common/CardScannedDocument";
import CardScanner from "react-lib/apps/HISV3/common/CardScanner";

import CardRehabTreatmentPerformRecordUX from "./CardRehabTreatmentPerformRecordUX";
import TableTreatmentPerform from "./TableTreatmentPerform";

import { RehabOrderType } from "./RHBInterface";

import { beToAd } from "react-lib/utils/dateUtils";
import { DateTextBox } from "react-lib/apps/common";

type CardRehabTreatmentPerformRecordProps = {
  onEvent: (e: any) => any;
  setProp: (key: string, value: any, callback?: () => void) => any;
  scannedDocumentController: any;
  patientData?: any;
  rehabOrderActionLogList?: any[];
  rehabTreatmentPerform: RehabOrderType | null;
  rehabTreatmentPerformList?: RehabOrderType[];
  selectedRehabWorkList?: Record<string, any> | null;
  buttonLoadCheck: any;
  django?: any;
  errorMessage: any;
  languageUX: string;
  searchedItemListWithKey: any;
  selectedEncounter?: any;
  selectedPatient: any;
  userTokenize: any;
  isIncludePerformedRecord: boolean;
  onChangeIncludePerformedRecord: any;
  performStartDate: string;
  performEndDate: string;
  onChangePerformStartDate: any;
  onChangePerformEndDate: any;
};

const BUTTON_ACTIONS = {
  cancel: "CANCEL",
  save: "SAVE",
  search: "SEARCH"
};

const CARD_REHAB_TREATMENT_PERFORM = "CardRehabTreatmentPerformRecord";

const ACTION_SEARCH = `${CARD_REHAB_TREATMENT_PERFORM}_${BUTTON_ACTIONS.search}`; 
const ACTION_SAVE = `${CARD_REHAB_TREATMENT_PERFORM}_${BUTTON_ACTIONS.save}`;
const ACTION_CANCEL = `${CARD_REHAB_TREATMENT_PERFORM}_${BUTTON_ACTIONS.cancel}`;

const CardRehabTreatmentPerformRecord = (props: CardRehabTreatmentPerformRecordProps) => {
  const [openScanDocument, setOpenScanDocument] = useState<boolean>(false);
  const [openViewDocument, setOpenViewDocument] = useState<boolean>(false);
  const [modConfirmCancel, setModConfirmCancel] = useState<{
    id: number;
    note: string;
  } | null>(null);

  useEffect(
    () => () => {
      props.setProp("rehabTreatmentPerform", null);
      props.setProp("userTokenize", null);
      props.setProp("selectedRehabWorkList", null);
    },
    []
  );

  const mapOptions = useCallback(
    (items: any[]) =>
      items.filter(Boolean).map((item) => ({
        key: item.icd10_id,
        text: `[${item.icdcode}] [${item.medterm}] ${item.icdterm}`,
        value: item.icd10_id,
        content: `[${item.icdcode}] ${item.icdterm}`,
      })),
    []
  );

  const filterByIds = useMemo(
    () => props.rehabTreatmentPerform?.data.ScannedDocument_id ?? [],
    [props.rehabTreatmentPerform?.data]
  );

  const filteredTreatmentPerformList = useMemo(
    () =>
      (props.rehabTreatmentPerformList ?? []).filter((order) => {
        if (!props.isIncludePerformedRecord && order.status_name === 'PERFORMED') {
          return false;
        }

        if (props.selectedRehabWorkList) {
          return order.items.some(
            (item) =>
              item.plan_item_detail?.to_department_for_physiotherapist ===
              props.selectedRehabWorkList?.out_perform_div
          );
        }

        return true;
      }),
    [props.rehabTreatmentPerformList, props.selectedRehabWorkList, props.isIncludePerformedRecord]
  );

  const handleChangeValue = (e: SyntheticEvent, data: any) => {
    props.setProp(`rehabTreatmentPerform.${data.name}`, data.value);
  };

  const handleChangeTime = (name: string) => (value: string) => {
    props.setProp(`rehabTreatmentPerform.${name}`, value);
  };

  const handleChangeDate = (name: string) => (value: string) => {
    props.setProp(`rehabTreatmentPerform.${name}`, value);
  };

  const handleSearch = () => {
    props.onEvent({
      message: "getRhbTreatment",
      params: {
        btnAction: ACTION_SEARCH,
        card: CARD_REHAB_TREATMENT_PERFORM,
        performStartDate: props.performStartDate,
        performEndDate: props.performEndDate 
      },
    });
  }

  const handleSave = () => {
    const data = props.rehabTreatmentPerform;

    if (!data) {
      return;
    }

    props.onEvent({
      message: "updateRhbTreatmentPlan",
      params: {
        btnAction: ACTION_SAVE,
        card: CARD_REHAB_TREATMENT_PERFORM,
        data: {
          ...data,
          action: "PERFORM",
          finish: `${beToAd(data.endDate || "")?.format("YYYY-MM-DD")} ${data.endTime}`,
          start: `${beToAd(data.startDate || "")?.format("YYYY-MM-DD")} ${data.startTime}`,
        },
      },
    });
  };

  const handleEditPerform = () => {
    const data = props.rehabTreatmentPerform;

    if (!data) {
      return;
    }

    props.onEvent({
      message: "updateRhbTreatmentPlan",
      params: {
        btnAction: ACTION_SAVE,
        card: CARD_REHAB_TREATMENT_PERFORM,
        data: {
          ...data,
          action: "EDIT_PERFORM",
          finish: `${beToAd(data.endDate || "")?.format("YYYY-MM-DD")} ${data.endTime}`,
          start: `${beToAd(data.startDate || "")?.format("YYYY-MM-DD")} ${data.startTime}`,
        },
      },
    });
  };

  const handleOpenScanDocument = () => {
    setOpenScanDocument(true);
  };

  const handleCloseScanDocument = (idNames: Record<string, string> = {}) => {
    if (Object.keys(idNames).length > 0) {
      const selected = props.rehabTreatmentPerform;
      const scannedDocIds = selected?.data.ScannedDocument_id ?? [];
      const scannedDocNames = selected?.data.ScannedDocument_name ?? {};
      const ids = Object.keys(idNames).map(Number);

      props.onEvent({
        message: "updateRhbTreatmentOrder",
        params: {
          action: selected?.status_name === "PERFORMED" ? "EDIT_PERFORM" : "EDIT",
          id: selected?.id,
          scannedDocIds: [...scannedDocIds, ...ids],
          scannedDocNames: { ...scannedDocNames, ...idNames },
        },
      });
    }

    setOpenScanDocument(false);
  };

  const handleOpenViewDocument = () => {
    setOpenViewDocument(true);
  };

  const handleCloseViewDocument = () => {
    setOpenViewDocument(false);
  };

  const handleAddSecondary = () => {
    const secondary = props.rehabTreatmentPerform?.procedure?.secondary ?? [];
    const lastItem = secondary.slice(-1)[0];

    if (secondary.length > 0 && lastItem.icd10_id === null) {
      return;
    }

    secondary.push({ icd10_id: null, icdcode: "", icdterm: "string", medterm: null });

    props.setProp("rehabTreatmentPerform.procedure.secondary", [...secondary]);
  };

  const handleDeleteOrderItem = (id: number, btnAction: string) => {
    props.onEvent({
      message: "deleteRhbOrderItem",
      params: { id, btnAction, card: CARD_REHAB_TREATMENT_PERFORM },
    });
  };

  const handleChangeNote = (e: SyntheticEvent, data: TextAreaProps) => {
    if (modConfirmCancel) {
      setModConfirmCancel({ ...modConfirmCancel, note: String(data.value) });
    }
  };

  const handleCloseConfirmCancel = () => {
    setModConfirmCancel(null);
  };

  const handleOpenConfirmCancel = (data: Record<string, any>) => {
    setModConfirmCancel({
      id: data.id,
      note: "",
    });
  };

  const handleConfirmCancel = () => {
    if (!modConfirmCancel) {
      return;
    }

    props.onEvent({
      message: "updateRhbTreatmentOrder",
      params: {
        action: "CANCEL",
        id: modConfirmCancel.id,
        btnAction: ACTION_CANCEL,
        canceled_reason: modConfirmCancel.note,
        card: CARD_REHAB_TREATMENT_PERFORM,
        onSuccess: handleCloseConfirmCancel,
      },
    });
  };

  const handleUpdateTreatmentCode = (data: {
    action: string;
    id?: number;
    btnAction: string;
    index: number;
    order: number | null;
    product?: number | null;
    quantity?: string;
  }) => {
    props.onEvent({
      message: "updateRhbOrderBillItem",
      params: { ...data, card: CARD_REHAB_TREATMENT_PERFORM },
    });
  };

  return (
    <>
      <SnackMessage
        error={props.errorMessage?.[CARD_REHAB_TREATMENT_PERFORM]}
        success={null}
        onClose={() => {
          props.setProp(`errorMessage.${CARD_REHAB_TREATMENT_PERFORM}`, null);
        }}
      />

      <CardRehabTreatmentPerformRecordUX
        onEvent={props.onEvent}
        setProp={props.setProp}
        data={props.rehabTreatmentPerform}
        isPerformed={props.rehabTreatmentPerform?.status_name === "PERFORMED"}
        isIncludePerformed={props.isIncludePerformedRecord}
        onChangeIncludePerformedRecord={props.onChangeIncludePerformedRecord}
        performStartDate={props.performStartDate}
        performEndDate={props.performEndDate}
        onChangePerformStartDate={props.onChangePerformStartDate}
        onChangePerformEndDate={props.onChangePerformEndDate}
        languageUX={props.languageUX}
        onChangeDate={handleChangeDate}
        onChangeValue={handleChangeValue}
        onOpenScanDocument={handleOpenScanDocument}
        onOpenViewDocument={handleOpenViewDocument}
        buttonAddSecondary={
          <div
            style={{
              alignItems: "center",
              color: "rgba(0, 114, 188, 1)",
              cursor: "pointer",
              display: "flex",
              marginLeft: "1.25rem",
            }}
            onClick={handleAddSecondary}
          >
            <div style={{ marginRight: "3px" }}>+</div>
            <div style={{ textDecoration: "underline" }}>Secondary procedure </div>
          </div>
        }
        performStartDateTextBox={
          <DateTextBox
            value={props.performStartDate}
            onChange={props.onChangePerformStartDate}
            inputStyle={{ width: "130px" }}
          />
        }
        performEndDateTextBox={
          <DateTextBox
            value={props.performEndDate}
            onChange={props.onChangePerformEndDate}
            inputStyle={{ width: "130px" }}
          />
        }
        buttonSearch={
          <ButtonLoadCheck
            setProp={props.setProp}
            color="blue"
            paramKey={ACTION_SEARCH}
            size="medium"
            title="SEARCH"
            buttonLoadCheck={props.buttonLoadCheck?.[ACTION_SEARCH]}
            onClick={handleSearch}
          />
        }
        buttonSave={
          <>
            {props.rehabTreatmentPerform?.status_name === "PERFORMED" ? (
              <ButtonLoadCheck
                setProp={props.setProp}
                color="yellow"
                disabled={!props.userTokenize?.employeeName}
                paramKey={ACTION_SAVE}
                size="medium"
                title="EDIT"
                buttonLoadCheck={props.buttonLoadCheck?.[ACTION_SAVE]}
                onClick={handleEditPerform}
              />
            ) : (
              <ButtonLoadCheck
                setProp={props.setProp}
                color="green"
                paramKey={ACTION_SAVE}
                size="medium"
                title="SAVE"
                buttonLoadCheck={props.buttonLoadCheck?.[ACTION_SAVE]}
                onClick={handleSave}
              />
            )}
          </>
        }
        employeeToken={
          <EmployeeToken
            autoComplete="one-time-code"
            employeeCode={props.userTokenize?.employeeCode}
            employeeName={props.userTokenize?.employeeName}
            error={!!props.userTokenize?.error}
            loading={props.userTokenize?.loading}
            placeholder="Employee code"
            onClearToken={() => {
              props.setProp("userTokenize", {
                ...props.userTokenize,
                employeeName: "",
                error: null,
                loading: false,
                token: "",
              });
            }}
            onEnterToken={(code) => {
              props.onEvent({
                message: "UserTokenize",
                params: { action: "CheckUserToken", code },
              });
            }}
          />
        }
        endTime={
          <TimeComboBox
            defaultValue={props.rehabTreatmentPerform?.endTime}
            placeholder=""
            fluid
            onTextChange={handleChangeTime("endTime")}
          />
        }
        principalProcedure={
          <SearchBoxWithKey
            onEvent={props.onEvent}
            id="ICD9CMCodeTermMedTerm_RTP"
            dropdownStyle={{ width: "29rem" }}
            placeholder="Search Medical term/ICD-09 term/ ICD-09 Code"
            selectedItem={props.rehabTreatmentPerform?.procedure?.principle?.icd10_id}
            size="small"
            type="ICD9CMCodeTermMedTerm"
            valueKey="icd10_id"
            inline
            searchedItemListWithKey={props.searchedItemListWithKey}
            mapOptions={mapOptions}
            setSelected={(value, key, data) => {
              props.setProp("rehabTreatmentPerform.procedure.principle", data);
            }}
            defaultText={
              props.rehabTreatmentPerform?.procedure?.principle?.icdcode
                ? `[${props.rehabTreatmentPerform.procedure.principle.icdcode}] ${props.rehabTreatmentPerform.procedure.principle.icdterm}`
                : ""
            }
          />
        }
        secondaryProcedure={props.rehabTreatmentPerform?.procedure?.secondary.map((item, index) => (
          <div
            key={`secondary-${item.icd10_id}`}
            style={{
              display: "flex",
              marginTop: index === 0 ? "" : "0.75rem",
            }}
          >
            <SearchBoxWithKey
              onEvent={props.onEvent}
              id={`ICD9CMCodeTermMedTerm_RTP_${index}`}
              defaultText={item.icdcode ? `[${item.icdcode}] ${item.icdterm}` : ""}
              dropdownStyle={{ width: "29rem" }}
              placeholder="Search Medical term/ICD-09 term/ ICD-09 Code"
              selectedItem={item.icd10_id}
              size="small"
              type="ICD9CMCodeTermMedTerm"
              valueKey="icd10_id"
              inline
              searchedItemListWithKey={props.searchedItemListWithKey}
              mapOptions={mapOptions}
              setSelected={(value, key, data) => {
                props.setProp(`rehabTreatmentPerform.procedure.secondary.${index}`, data);
              }}
            />
            <Icon
              style={{ padding: "0.5rem 0 0 0.75rem" }}
              color="red"
              name="minus circle"
              link
              onClick={() => {
                const secondary = props.rehabTreatmentPerform?.procedure?.secondary ?? [];

                secondary.splice(index, 1);

                props.setProp("rehabTreatmentPerform.procedure.secondary", [...secondary]);
              }}
            />
          </div>
        ))}
        startTime={
          <TimeComboBox
            defaultValue={props.rehabTreatmentPerform?.startTime}
            placeholder=""
            fluid
            onTextChange={handleChangeTime("startTime")}
          />
        }
        treatmentPerform={
          <TableTreatmentPerform
            onEvent={props.onEvent}
            setProp={props.setProp}
            rehabOrderActionLogList={props.rehabOrderActionLogList}
            rehabTreatmentPerform={props.rehabTreatmentPerform}
            rehabTreatmentPerformList={filteredTreatmentPerformList}
            buttonLoadCheck={props.buttonLoadCheck}
            searchedItemListWithKey={props.searchedItemListWithKey}
            onCancelOrder={handleOpenConfirmCancel}
            onDeleteOrderItem={handleDeleteOrderItem}
            onUpdateTreatmentCode={handleUpdateTreatmentCode}
          />
        }
      />

      <Modal open={openScanDocument}>
        <CardScanner
          controller={props.scannedDocumentController}
          categoryCode="RHB"
          encounterId={props.selectedEncounter?.id}
          ownerDivision={props.django?.division}
          patientId={props.selectedEncounter?.patient_id}
          patientInfo={props.selectedPatient}
          disabledDocCategory
          disabledDocType
          hideDoctor
          django={props.django}
          onClose={handleCloseScanDocument}
        />
      </Modal>

      <Modal open={openViewDocument} closeOnDimmerClick onClose={handleCloseViewDocument}>
        <CardScannedDocument
          controller={props.scannedDocumentController}
          filterByIds={filterByIds}
          patientData={props.patientData}
          hideFilter
          onClose={handleCloseViewDocument}
          patientInfo={{
            ...props.selectedPatient,
            encounter_id: props.selectedEncounter?.id,
          }}
        />
      </Modal>

      <ModConfirm
        denyButtonStyle={{ marginLeft: "-1rem", width: "14rem" }}
        denyButtonText="Cancel the cancellation"
        modalStyle={{ width: "40rem" }}
        openModal={!!modConfirmCancel}
        titleColor="red"
        onCloseWithDimmerClick={handleCloseConfirmCancel}
        onDeny={handleCloseConfirmCancel}
        approveButton={
          <ButtonLoadCheck
            setProp={props.setProp}
            style={{ width: "14rem" }}
            color="green"
            paramKey={ACTION_CANCEL}
            size="medium"
            title="Confirm Cancellation"
            basic
            buttonLoadCheck={props.buttonLoadCheck?.[ACTION_CANCEL]}
            onClick={handleConfirmCancel}
          />
        }
        content={
          <Form style={{ margin: "-0.5rem 0 -0.75rem" }}>
            <Form.Field>
              <div
                style={{
                  fontWeight: "bold",
                  marginBottom: "0.5rem",
                }}
              >
                Please specify the reason for canceling the treatment perform
              </div>
              <TextArea value={modConfirmCancel?.note || ""} onChange={handleChangeNote} />
            </Form.Field>
          </Form>
        }
      />
    </>
  );
};

CardRehabTreatmentPerformRecord.displayName = "CardRehabTreatmentPerformRecord";

export default React.memo(CardRehabTreatmentPerformRecord);
